import type { Sort } from "@/lib/sortRows";

// Every control on this page keeps its state in the URL — the tab, which rows
// are open, which view is in force. They have to compose, so each link is
// built from the current parameters rather than from scratch.

export const OPEN_PARAM = "open";
export const VIEW_PARAM = "view";

// One control rather than a status filter plus a separate archived toggle:
// two controls governing the same rows can disagree, and reconciling them was
// special-case code that existed only to stop them contradicting each other.
//
// ACTIVE is the working set and the default — templates move
// DRAFT -> PUBLISHED -> ARCHIVED, and retired content is not what the listing
// is opened to look at. With content arriving from a generator, "what is still
// unreviewed" is the next question it gets asked, so DRAFT stays first-class.
export const QUEST_VIEWS = [
  "ACTIVE",
  "DRAFT",
  "PUBLISHED",
  "ARCHIVED",
] as const;

export type QuestView = (typeof QUEST_VIEWS)[number];

export const DEFAULT_VIEW: QuestView = "ACTIVE";

export type QuestsSearchParams = Record<string, string | string[] | undefined>;

const readOne = (raw: string | string[] | undefined): string | undefined =>
  typeof raw === "string" ? raw : undefined;

// An unreadable view falls back to the default rather than showing nothing:
// a hand-edited URL should land somewhere useful.
export const parseView = (raw: string | string[] | undefined): QuestView =>
  QUEST_VIEWS.find((view) => view === readOne(raw)) ?? DEFAULT_VIEW;

// ACTIVE is the only view that spans two statuses; the rest name one.
export const isTemplateInView = (status: string, view: QuestView): boolean =>
  view === "ACTIVE" ? status !== "ARCHIVED" : status === view;

export const parseOpenKeys = (raw: string | string[] | undefined): string[] => {
  const value = readOne(raw);

  return value === undefined || value === "" ? [] : value.split(",");
};

const toQuery = (params: URLSearchParams): string => {
  const query = params.toString();

  return query === "" ? "/quests" : `/quests?${query}`;
};

// Carries every other parameter through, so opening a row does not silently
// reset the tab or the view.
const currentParams = (searchParams: QuestsSearchParams): URLSearchParams => {
  const params = new URLSearchParams();

  Object.entries(searchParams).forEach(([key, value]) => {
    const single = readOne(value);

    if (single !== undefined && single !== "") {
      params.set(key, single);
    }
  });

  return params;
};

export const buildToggleRowHref = (
  searchParams: QuestsSearchParams,
  id: number,
): string => {
  const params = currentParams(searchParams);
  const open = parseOpenKeys(searchParams[OPEN_PARAM]);
  const key = String(id);
  const next = open.includes(key)
    ? open.filter((value) => value !== key)
    : [...open, key];

  if (next.length === 0) {
    params.delete(OPEN_PARAM);
  } else {
    params.set(OPEN_PARAM, next.join(","));
  }

  return toQuery(params);
};

// The default view leaves the parameter off, so the plain /quests URL is the
// one people land on and share.
export const buildViewHref = (
  searchParams: QuestsSearchParams,
  view: QuestView,
): string => {
  const params = currentParams(searchParams);

  if (view === DEFAULT_VIEW) {
    params.delete(VIEW_PARAM);
  } else {
    params.set(VIEW_PARAM, view);
  }

  return toQuery(params);
};

// One sort parameter per tab rather than one shared: the tabs list different
// row types with different columns, so a shared key would go unreadable the
// moment you switched tab.
export const TEMPLATE_SORT_PARAM = "sort";
export const QUEST_SORT_PARAM = "qsort";

// "-updatedAt" for descending: short enough to read in the address bar and
// needs no escaping.
const DESCENDING_PREFIX = "-";

export const parseSort = (
  raw: string | string[] | undefined,
  allowedKeys: readonly string[],
): Sort | undefined => {
  const value = readOne(raw);

  if (value === undefined || value === "") {
    return undefined;
  }

  const isDescending = value.startsWith(DESCENDING_PREFIX);
  const key = isDescending ? value.slice(DESCENDING_PREFIX.length) : value;

  // An unsortable column falls back to the default order, the same way an
  // unreadable view falls back to the default view.
  return allowedKeys.includes(key) ? { key, isDescending } : undefined;
};

// Three states rather than two: the default order is not reproducible by any
// single column — archived templates sink, then the newest lead — so clicking
// a third time has to be able to give it back.
const nextSort = (key: string, current: Sort | undefined): Sort | undefined => {
  if (current?.key !== key) {
    return { key, isDescending: false };
  }

  return current.isDescending ? undefined : { key, isDescending: true };
};

export const buildSortHref = (
  searchParams: QuestsSearchParams,
  param: string,
  key: string,
  current: Sort | undefined,
): string => {
  const params = currentParams(searchParams);
  const next = nextSort(key, current);

  if (next === undefined) {
    params.delete(param);
  } else {
    params.set(
      param,
      `${next.isDescending ? DESCENDING_PREFIX : ""}${next.key}`,
    );
  }

  return toQuery(params);
};
